import { Facebook, Instagram, Linkedin, Heart, Plane } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'

const navigation = {
  main: [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Packages', href: '/packages' },
    { name: 'Reviews', href: '/#reviews' },
    { name: 'Contact', href: '/contact' },
  ],
}

export default function Footer() {
  const { franchiseData, loading } = useFranchiseData()

  if (loading || !franchiseData) {
    return (
      <footer className="bg-white border-t border-gray-200">
        <div className="mx-auto max-w-7xl overflow-hidden px-6 py-12 sm:py-16 lg:px-8">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 rounded w-48 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-32"></div>
          </div>
        </div>
      </footer>
    )
  }

  const socialLinks = [
    {
      name: 'Facebook',
      href: franchiseData.facebook || '#',
      icon: Facebook,
    },
    {
      name: 'Instagram', 
      href: franchiseData.instagram || '#',
      icon: Instagram,
    },
    {
      name: 'LinkedIn',
      href: franchiseData.linkedin || '#',
      icon: Linkedin,
    },
  ].filter(link => link.href !== '#') // Only show links that exist

  return (
    <footer className="bg-[#006D77] text-gray-200">
      <div className="mx-auto max-w-7xl overflow-hidden px-6 py-12 sm:py-16 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-2">
              <span className="text-2xl font-bold text-white">{franchiseData.name}</span>
            </div>
            <p className="mt-4 text-base text-gray-300">
              Your trusted travel partner for unforgettable experiences around the world. 
              We create memories that last a lifetime.
            </p>
            <div className="mt-4 flex items-center">
              <span className="text-white font-bold mr-2">za</span>
              <span className="text-white font-semibold">Proudly South African</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-1">
            <h3 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">Quick Links</h3>
            <ul className="space-y-4">
              {navigation.main.map((item) => (
                <li key={item.name}>
                  <a href={item.href} className="text-gray-300 hover:text-white transition-colors text-base">
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details */}
          <div className="lg:col-span-1">
            <h3 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">Contact Info</h3>
            <div className="space-y-4">
              {/* Rene Contact */}
              <div className="space-y-2">
                <div className="text-white font-medium">Rene</div>
                <div className="flex items-center space-x-2 text-sm text-gray-300">
                  <span className="text-white"><svg xmlns='http://www.w3.org/2000/svg' className='inline h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 5a2 2 0 012-2h3.28a2 2 0 011.7.95l1.54 2.6a2 2 0 01-.17 2.18l-1.1 1.65a11.042 11.042 0 005.516 5.516l1.65-1.1a2 2 0 012.18-.17l2.6 1.54a2 2 0 01.95 1.7V19a2 2 0 01-2 2h-1C7.82 21 3 16.18 3 10V9a2 2 0 012-2z' /></svg></span>
                  <span>+27 60 263 0009</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-300">
                  <span className="text-white"><svg xmlns='http://www.w3.org/2000/svg' className='inline h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 12H8m8 0a4 4 0 11-8 0 4 4 0 018 0zm0 0v1a4 4 0 01-8 0v-1' /></svg></span>
                  <span>rene@mementotravelco.co.za</span>
                </div>
              </div>

              {/* Werner Contact */}
              <div className="space-y-2">
                <div className="text-white font-medium">Werner</div>
                <div className="flex items-center space-x-2 text-sm text-gray-300">
                  <span className="text-white"><svg xmlns='http://www.w3.org/2000/svg' className='inline h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 5a2 2 0 012-2h3.28a2 2 0 011.7.95l1.54 2.6a2 2 0 01-.17 2.18l-1.1 1.65a11.042 11.042 0 005.516 5.516l1.65-1.1a2 2 0 012.18-.17l2.6 1.54a2 2 0 01.95 1.7V19a2 2 0 01-2 2h-1C7.82 21 3 16.18 3 10V9a2 2 0 012-2z' /></svg></span>
                  <span>+27 69 368 8545</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-300">
                  <span className="text-white"><svg xmlns='http://www.w3.org/2000/svg' className='inline h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 12H8m8 0a4 4 0 11-8 0 4 4 0 018 0zm0 0v1a4 4 0 01-8 0v-1' /></svg></span>
                  <span>werner@mementotravelco.co.za</span>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="space-y-2">
                <div className="text-white font-medium">Emergency</div>
                <div className="flex items-center space-x-2 text-sm text-gray-300">
                  <span className="text-white"><svg xmlns='http://www.w3.org/2000/svg' className='inline h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 5a2 2 0 012-2h3.28a2 2 0 011.7.95l1.54 2.6a2 2 0 01-.17 2.18l-1.1 1.65a11.042 11.042 0 005.516 5.516l1.65-1.1a2 2 0 012.18-.17l2.6 1.54a2 2 0 01.95 1.7V19a2 2 0 01-2 2h-1C7.82 21 3 16.18 3 10V9a2 2 0 012-2z' /></svg></span>
                  <span>+27 63 805 9489</span>
                </div>
              </div>

              <div className="text-sm text-gray-400 mt-2">Office Hours: Mon-Fri 9AM-4PM </div>
              <div className="text-sm text-gray-400 mt-2">Saturday & Sunday: Available for travel emergencies. </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="lg:col-span-1">
            <h3 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">Social Media</h3>
            <p className="mb-4 text-base text-gray-300">Follow us for travel inspiration</p>
            <div className="flex space-x-4">
              {socialLinks.map((item) => (
                <a key={item.name} href={item.href} className="bg-[#232b36] rounded-full p-3 text-white hover:bg-white hover:text-gray-900 transition-colors">
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="h-6 w-6" aria-hidden="true" />
                </a>
              ))}
              {franchiseData.whatsapp && (
                <a 
                  href={`https://wa.me/${franchiseData.whatsapp.replace(/[^\d]/g, '')}`}
                  className="bg-[#232b36] rounded-full p-3 text-white hover:bg-white hover:text-gray-900 transition-colors"
                >
                  <span className="sr-only">WhatsApp</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 border-t border-[#232b36] pt-8">
          <div className="flex flex-col items-center space-y-4 lg:flex-row lg:justify-between lg:space-y-0">
            <p className="text-xs text-gray-400">
              &copy; 2024 {franchiseData.name}. All rights reserved.
            </p>
            <p className="text-xs text-gray-400 flex items-center">
              Made with <Heart className="h-3 w-3 text-red-500 mx-1" /> for travelers
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
} 